import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TabType } from '@hunting-nature/shared-core/enums/tab-bar.enum';
import { NavController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { distinctUntilChanged, filter, from, map, switchMap, tap } from 'rxjs';

import { SettingsService } from '../../services/settings.service';
import { TabBarActions } from './tab-bar.actions';

@Injectable()
export class TabBarEffects {
  private readonly actions$ = inject(Actions);
  private readonly navController = inject(NavController);
  private readonly router = inject(Router);
  private readonly settingsService = inject(SettingsService);

  // Persist the selected tab so it can be restored on the next app start.
  persistTab$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TabBarActions.selectTab, TabBarActions.syncActiveTabFromRoute),
        switchMap(({ tab }) => from(this.settingsService.setLastTab$(tab)))
      ),
    { dispatch: false }
  );

  selectTab$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TabBarActions.selectTab),
        tap(({ tab }) => {
          const targetRoute = this.getRouteForTab(tab);
          if (!this.router.url.startsWith(targetRoute)) {
            void this.navController.navigateRoot([targetRoute]);
          }
        })
      ),
    { dispatch: false }
  );

  // selectTab only fires on tab-button clicks / ionViewWillEnter — in-tab navigation (e.g. camera details → back) skips it, leaving activeTab stale and the FAB menu empty.
  syncActiveTabFromRoute$ = createEffect(() =>
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => this.getTabFromUrl(event.urlAfterRedirects)),
      filter((tab): tab is TabType => tab !== undefined),
      distinctUntilChanged(),
      map(tab => TabBarActions.syncActiveTabFromRoute({ tab }))
    )
  );

  private getRouteForTab(tab: TabType): string {
    switch (tab) {
      case TabType.Gallery:
        return '/home/gallery';
      case TabType.Cameras:
        return '/home/cameras';
      /* Ballistics & Map pages to be restored in future release 
      case TabType.Ballistics:
        return '/home/ballistics';
      case TabType.Map:
        return '/home/map'; */
    }
  }

  private getTabFromUrl(url: string): TabType | undefined {
    if (url.includes('/gallery')) return TabType.Gallery;
    if (url.includes('/cameras')) return TabType.Cameras;
    /* Ballistics & Map pages to be restored in future release 
    if (url.includes('/ballistics')) return TabType.Ballistics;
    if (url.includes('/map')) return TabType.Map; */
    return undefined;
  }
}
